import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Catalog.css';

// Catalog images - organized by category for the tabbed display
// Images are served from public/catalog folder

// Sizing note for jeans - displayed in modal and descriptions
const JEANS_SIZING_NOTE = "Order 1.5\" above your regular length for cuffing.";

const catalogData = {
  tops: [
    {
      id: 'heritage-ocbd-white',
      name: 'Heritage OCBD - White',
      image: '/catalog/Heritage OCBD Shirt - White.PNG',
      description: 'The quintessential prep essential. Premium Oxford cloth with a perfect roll collar.',
      quality: 'Japanese Oxford cloth, mother-of-pearl buttons, locker loop',
      category: 'Tops'
    },
    {
      id: 'heritage-ocbd-blue',
      name: 'Heritage OCBD - Blue',
      image: '/catalog/Heritage OCBD Shirt - Blue.PNG',
      description: 'Classic light blue Oxford. The foundation of any refined wardrobe.',
      quality: 'Japanese Oxford cloth, mother-of-pearl buttons, locker loop',
      category: 'Tops'
    },
    {
      id: 'pima-ocbd-white',
      name: 'Pima Cotton OCBD - White',
      image: '/catalog/Pima Cotton OCBD Shirt - White.jpg',
      description: 'Extra-soft Pima cotton Oxford for supreme comfort. Crisp yet comfortable.',
      quality: 'Peruvian Pima cotton, single-needle construction',
      category: 'Tops'
    },
    {
      id: 'pima-ocbd-blue',
      name: 'Pima Cotton OCBD - Blue',
      image: '/catalog/Pima Cotton OCBD Shirt - Blue.jpg',
      description: 'Luxurious Pima cotton in a beautiful blue. Soft hand feel, refined appearance.',
      quality: 'Peruvian Pima cotton, single-needle construction',
      category: 'Tops'
    },
    {
      id: 'twill-dress-black',
      name: 'ELS Cotton Twill Shirt - Black',
      image: '/catalog/ELS Cotton Twill Dress Shirt - Black.PNG',
      description: 'Extra-long staple cotton in a substantial twill weave. Sleek black for modern edge.',
      quality: 'ELS Egyptian cotton, French seams, split yoke',
      category: 'Tops'
    },
    {
      id: 'twill-dress-blue',
      name: 'ELS Cotton Twill Shirt - Blue',
      image: '/catalog/ELS Cotton Twill Dress Shirt - Blue.PNG',
      description: 'Premium twill in a sophisticated blue. The ultimate dress shirt.',
      quality: 'ELS Egyptian cotton, French seams, split yoke',
      category: 'Tops'
    },
    {
      id: 'twill-dress-white',
      name: 'ELS Cotton Twill Shirt - White',
      image: '/catalog/ELS Cotton Twill Dress Shirt - White.PNG',
      description: 'Crisp white in exceptional ELS cotton twill. Boardroom ready.',
      quality: 'ELS Egyptian cotton, French seams, split yoke',
      category: 'Tops'
    },
    {
      id: 'poplin-dress-blue',
      name: 'ELS Cotton Poplin Shirt - Blue',
      image: '/catalog/ELS Cotton Poplin Dress Shirt - Blue.PNG',
      description: 'Lightweight poplin in extra-long staple cotton. Perfect for warmer days.',
      quality: 'ELS Egyptian cotton, mother-of-pearl buttons',
      category: 'Tops'
    },
    {
      id: 'poplin-dress-white',
      name: 'ELS Cotton Poplin Shirt - White',
      image: '/catalog/ELS Cotton Poplin Dress Shirt - White.PNG',
      description: 'Classic white poplin with luxurious hand feel. Essential elegance.',
      quality: 'ELS Egyptian cotton, mother-of-pearl buttons',
      category: 'Tops'
    },
    {
      id: 'polo-black',
      name: 'Supima Cotton Polo - Black',
      image: '/catalog/Supima Cotton Polo Shirt - Black.PNG',
      description: 'Premium Supima cotton polo in sleek black. Casual refinement perfected.',
      quality: 'American Supima cotton, ribbed collar and cuffs',
      category: 'Tops'
    },
    {
      id: 'polo-gray',
      name: 'Supima Cotton Polo - Gray',
      image: '/catalog/Supima Cotton Polo Shirt - Gray.PNG',
      description: 'Versatile gray polo in ultra-soft Supima. The smart casual essential.',
      quality: 'American Supima cotton, ribbed collar and cuffs',
      category: 'Tops'
    },
    {
      id: 'polo-white',
      name: 'Supima Cotton Polo - White',
      image: '/catalog/Supima Cotton Polo Shirt - White.PNG',
      description: 'Classic white polo elevated with Supima cotton. Timeless sophistication.',
      quality: 'American Supima cotton, ribbed collar and cuffs',
      category: 'Tops'
    },
    {
      id: 'tshirt-black',
      name: 'Supima Cotton T-Shirt - Black',
      image: '/catalog/Supima Cotton T-shirt - Black.PNG',
      description: 'The perfect black tee. Premium Supima cotton that holds its shape.',
      quality: 'American Supima cotton, seamless collar',
      category: 'Tops'
    },
    {
      id: 'tshirt-gray',
      name: 'Supima Cotton T-Shirt - Gray',
      image: '/catalog/Supima Cotton T-shirt - Gray.PNG',
      description: 'Essential gray tee in exceptional cotton. Elevated basics done right.',
      quality: 'American Supima cotton, seamless collar',
      category: 'Tops'
    },
    {
      id: 'tshirt-white',
      name: 'Supima Cotton T-Shirt - White',
      image: '/catalog/Supima Cotton T-shirt - White.PNG',
      description: 'Crisp white tee that actually lasts. Premium construction throughout.',
      quality: 'American Supima cotton, seamless collar',
      category: 'Tops'
    }
  ],
  midLayers: [
    {
      id: 'cashmere-vneck-black',
      name: 'Cashmere V-Neck - Black',
      image: '/catalog/Cashmere V-Neck Sweaters - Black.PNG',
      description: 'Essential black cashmere in a refined V-neck silhouette. Pairs perfectly with OCBDs and leather jackets.',
      quality: '100% Grade-A Mongolian cashmere, 2-ply construction',
      category: 'Mid Layers'
    },
    {
      id: 'cashmere-vneck-charcoal',
      name: 'Cashmere V-Neck - Charcoal',
      image: '/catalog/Cashmere V-Neck Sweaters - Charcoal.PNG',
      description: 'Sophisticated charcoal cashmere for effortless layering. A cornerstone of the refined wardrobe.',
      quality: '100% Grade-A Mongolian cashmere, 2-ply construction',
      category: 'Mid Layers'
    },
    {
      id: 'cashmere-vneck-gray',
      name: 'Cashmere V-Neck - Gray',
      image: '/catalog/Cashmere V-Neck Sweaters - Gray.PNG',
      description: 'Versatile heather gray cashmere that works with everything in your wardrobe.',
      quality: '100% Grade-A Mongolian cashmere, 2-ply construction',
      category: 'Mid Layers'
    },
    {
      id: 'cashmere-vneck-navy',
      name: 'Cashmere V-Neck - Navy',
      image: '/catalog/Cashmere V-Neck Sweaters - Navy.PNG',
      description: 'Classic navy cashmere with timeless appeal. The prep essential with edge potential.',
      quality: '100% Grade-A Mongolian cashmere, 2-ply construction',
      category: 'Mid Layers'
    },
    {
      id: 'cashmere-vneck-lightblue',
      name: 'Cashmere V-Neck - Light Blue',
      image: '/catalog/Cashmere V-Neck Sweaters - Light Blue.PNG',
      description: 'Soft light blue cashmere for a refined, approachable look. Perfect for spring layering.',
      quality: '100% Grade-A Mongolian cashmere, 2-ply construction',
      category: 'Mid Layers'
    },
    {
      id: 'cashmere-crew-charcoal',
      name: 'Cashmere Crewneck - Charcoal',
      image: '/catalog/Cashmere Creweck Sweaters - Charcoal.PNG',
      description: 'Classic crewneck silhouette in luxurious cashmere. Relaxed sophistication at its finest.',
      quality: '100% Grade-A Mongolian cashmere, ribbed cuffs and hem',
      category: 'Mid Layers'
    },
    {
      id: 'cashmere-crew-gray',
      name: 'Cashmere Crewneck - Gray',
      image: '/catalog/Cashmere Creweck Sweaters - Gray.PNG',
      description: 'Versatile gray crewneck cashmere for everyday luxury.',
      quality: '100% Grade-A Mongolian cashmere, ribbed cuffs and hem',
      category: 'Mid Layers'
    },
    {
      id: 'cashmere-crew-navy',
      name: 'Cashmere Crewneck - Navy',
      image: '/catalog/Cashmere Creweck Sweaters - Navy.PNG',
      description: 'Timeless navy crewneck in the softest cashmere. A wardrobe foundation piece.',
      quality: '100% Grade-A Mongolian cashmere, ribbed cuffs and hem',
      category: 'Mid Layers'
    },
    {
      id: 'merino-crew-gray',
      name: 'Merino Crewneck - Gray',
      image: '/catalog/Merino Crewneck Sweaters - Gray.PNG',
      description: 'Fine merino wool for everyday wear. Temperature regulating and machine washable.',
      quality: 'Extra-fine Australian merino wool, fully fashioned',
      category: 'Mid Layers'
    },
    {
      id: 'merino-vneck-gray',
      name: 'Merino V-Neck - Gray',
      image: '/catalog/Merino V-Neck Sweaters - Gray.PNG',
      description: 'Classic V-neck in durable merino wool. Perfect for office to evening transitions.',
      quality: 'Extra-fine Australian merino wool, fully fashioned',
      category: 'Mid Layers'
    }
  ],
  outerwear: [
    // Leather Jackets - American Made
    {
      id: 'american-horsehide-jacket',
      name: 'American Made Horsehide Leather Jacket',
      image: '/catalog/American Made Horsehide Leather Jacket.jpg',
      description: 'Premium American-crafted horsehide leather jacket. Legendary durability meets refined tailoring. Develops exceptional patina over decades of wear.',
      quality: 'American horsehide leather, American made, fully lined',
      category: 'Outerwear'
    },
    {
      id: 'american-steerhide-jacket',
      name: 'American Made Steerhide Leather Jacket',
      image: '/catalog/American Made Steerhide Leather Jacket.jpg',
      description: 'Robust American steerhide in classic jacket silhouette. Built in the USA with heritage craftsmanship. The ultimate investment piece.',
      quality: 'American steerhide leather, American made, brass hardware',
      category: 'Outerwear'
    },
    {
      id: 'american-horsehide-moto',
      name: 'American Made Horsehide Motorcycle Jacket',
      image: '/catalog/American Made Horsehide Motorcycle Jacket.jpg',
      description: 'The quintessential American motorcycle jacket in premium horsehide. Asymmetric zip, belted waist, made entirely in the USA.',
      quality: 'American horsehide leather, American made, YKK zippers',
      category: 'Outerwear'
    },
    {
      id: 'american-steerhide-moto',
      name: 'American Made Steerhide Motorcycle Jacket',
      image: '/catalog/American Made Steerhide Motorcycle Jacket.jpg',
      description: 'Classic motorcycle jacket crafted from American steerhide. Uncompromising quality from American artisans.',
      quality: 'American steerhide leather, American made, brass hardware',
      category: 'Outerwear'
    },
    // Leather Jackets - Import
    {
      id: 'horsehide-jacket',
      name: 'Horsehide Leather Jacket',
      image: '/catalog/Horsehide Leather Jacket.jpg',
      description: 'Premium horsehide leather jacket with exceptional grain and durability. Develops rich character with age.',
      quality: 'Horsehide leather, fully lined, quality hardware',
      category: 'Outerwear'
    },
    {
      id: 'steerhide-jacket',
      name: 'Steerhide Leather Jacket',
      image: '/catalog/Steerhide Leather Jacket.jpg',
      description: 'Robust steerhide leather in timeless jacket form. Built to last generations with proper care.',
      quality: 'Steerhide leather, fully lined, quality hardware',
      category: 'Outerwear'
    },
    {
      id: 'horsehide-moto',
      name: 'Horsehide Motorcycle Jacket',
      image: '/catalog/Horsehide Motorcycle Jacket.jpg',
      description: 'Classic motorcycle jacket in premium horsehide leather. Iconic asymmetric design with quality construction.',
      quality: 'Horsehide leather, quilted lining, YKK zippers',
      category: 'Outerwear'
    },
    {
      id: 'steerhide-moto',
      name: 'Steerhide Motorcycle Jacket',
      image: '/catalog/Steerhide Motorcycle Jacket.jpg',
      description: 'Rugged steerhide motorcycle jacket built for the road. Classic style that never fades.',
      quality: 'Steerhide leather, quilted lining, YKK zippers',
      category: 'Outerwear'
    },
    // Lambskin
    {
      id: 'lambskin-jacket',
      name: 'Lambskin Leather Jacket',
      image: '/catalog/Full Grain Leather Shirt Jacket I.PNG',
      description: 'Butter-soft lambskin in a refined jacket silhouette. Lightweight luxury for transitional seasons.',
      quality: 'Full-grain lambskin leather, silk-blend lining',
      category: 'Outerwear'
    },
    // Other Leather
    {
      id: 'leather-blazer',
      name: 'Full-Grain Leather Blazer',
      image: '/catalog/Full Grain Leather Blazer.PNG',
      description: 'Where boardroom meets rebellion. This leather blazer merges tailored sophistication with rugged character. Perfect for commanding any room.',
      quality: 'Full-grain lambskin leather, fully lined interior',
      category: 'Outerwear'
    },
    {
      id: 'napa-blazer',
      name: 'Genuine Napa Leather Blazer',
      image: '/catalog/Genuine Napa Leather Blazer.PNG',
      description: 'Butter-soft Napa leather in a refined blazer silhouette. Supremely comfortable with unmatched elegance.',
      quality: 'Genuine Napa leather, silk-blend lining',
      category: 'Outerwear'
    },
    {
      id: 'leather-vest',
      name: 'Full-Grain Leather Vest',
      image: '/catalog/Full Grain Leather Vest.PNG',
      description: 'A versatile layering piece that adds instant edge to any outfit. Layer over OCBDs or sweaters for the perfect prep-edge blend.',
      quality: 'Full-grain cowhide leather, adjustable back',
      category: 'Outerwear'
    },
    {
      id: 'leather-shirt-jacket-2',
      name: 'Leather Shirt Jacket',
      image: '/catalog/Full Grain Leather Shirt Jacket II.PNG',
      description: 'A refined take on the classic shirt jacket. Features chest pockets and a streamlined fit.',
      quality: 'Full-grain sheepskin leather, button front',
      category: 'Outerwear'
    },
    // Non-Leather Jackets
    {
      id: 'denim-blazer',
      name: 'Premium Denim Blazer',
      image: '/catalog/Denim Blazer.PNG',
      description: 'Japanese denim meets tailored structure. Bridges the gap between casual and refined with effortless cool.',
      quality: 'Japanese denim, canvas lining, horn buttons',
      category: 'Outerwear'
    },
    {
      id: 'canvas-blazer-gray',
      name: 'Canvas Blazer - Slate Gray',
      image: '/catalog/Canvas Blazer - Gray.jpg',
      description: 'Rugged canvas construction with tailored precision. Perfect for transitional weather and smart-casual occasions.',
      quality: 'Heavy-weight canvas, fully lined, natural shell buttons',
      category: 'Outerwear'
    },
    {
      id: 'canvas-blazer-navy',
      name: 'Canvas Blazer - Navy',
      image: '/catalog/Canvas Blazer - Navy.jpg',
      description: 'A timeless navy blazer reimagined in durable canvas. Refined enough for meetings, rugged enough for weekend adventures.',
      quality: 'Heavy-weight canvas, fully lined, natural shell buttons',
      category: 'Outerwear'
    },
    {
      id: 'waxed-canvas-black',
      name: 'Waxed Canvas Shirt Jacket - Black',
      image: '/catalog/Waxed Canvas Shirt Jacket - Black.PNG',
      description: 'British heritage meets modern edge. Water-resistant waxed canvas that only gets better with age.',
      quality: 'Waxed cotton canvas, corduroy collar, brass snaps',
      category: 'Outerwear'
    },
    {
      id: 'waxed-canvas-gray',
      name: 'Waxed Canvas Shirt Jacket - Gray',
      image: '/catalog/Waxed Canvas Shirt Jacket - Gray.PNG',
      description: 'Versatile slate gray waxed canvas. Layers perfectly over shirts and sweaters.',
      quality: 'Waxed cotton canvas, blanket lining',
      category: 'Outerwear'
    },
    {
      id: 'waxed-canvas-navy',
      name: 'Waxed Canvas Shirt Jacket - Navy',
      image: '/catalog/Waxed Canvas Shirt Jacket - Navy.PNG',
      description: 'Classic navy in rugged waxed canvas. The essential layering piece for fall and spring.',
      quality: 'Waxed cotton canvas, blanket lining',
      category: 'Outerwear'
    },
    {
      id: 'selvedge-denim-jacket',
      name: 'Selvedge Denim Shirt Jacket',
      image: '/catalog/Selvedge Denim Shirt Jacket.PNG',
      description: 'Premium selvedge denim in a substantial shirt jacket cut. Features exposed selvedge details on interior.',
      quality: 'Japanese selvedge denim, copper rivets',
      category: 'Outerwear'
    },
    {
      id: 'dark-wash-denim-jacket',
      name: 'Dark Wash Denim Shirt Jacket',
      image: '/catalog/Dark Wash Denim Shirt Jacket.jpg',
      description: 'Deep indigo raw denim that will fade beautifully with wear. A wardrobe essential.',
      quality: 'Japanese raw denim, sanforized, copper buttons',
      category: 'Outerwear'
    },
    {
      id: 'denim-trenchcoat',
      name: 'Denim Trenchcoat',
      image: '/catalog/Denim Trenchcoat - Black.jpg',
      description: 'A statement piece that redefines outerwear. Trench coat elegance meets denim durability.',
      quality: 'Heavy-weight denim, double-breasted closure',
      category: 'Outerwear'
    }
  ],
  bottoms: [
    // Light American Selvedge (14oz)
    {
      id: 'american-light-selvedge-straight',
      name: 'Light American Selvedge Jeans - Straight Fit',
      image: '/catalog/Light American Selvedge Jeans Straight.jpg',
      description: `Premium 14oz American selvedge denim in classic straight fit. Shuttle-loomed in the USA with traditional construction. ${JEANS_SIZING_NOTE}`,
      quality: '14oz American selvedge denim, chain-stitched hems, copper rivets',
      category: 'Bottoms',
      fit: 'Straight Fit'
    },
    {
      id: 'american-light-selvedge-tapered',
      name: 'Light American Selvedge Jeans - Straight Tapered',
      image: '/catalog/Light American Selvedge Jeans Tapered.jpg',
      description: `Premium 14oz American selvedge denim with modern straight-tapered silhouette. Classic construction, contemporary fit. ${JEANS_SIZING_NOTE}`,
      quality: '14oz American selvedge denim, chain-stitched hems, copper rivets',
      category: 'Bottoms',
      fit: 'Straight Tapered'
    },
    // Heavy American Selvedge (21oz)
    {
      id: 'american-heavy-selvedge-straight',
      name: 'Heavy American Selvedge Jeans - Straight Fit',
      image: '/catalog/Heavy American Selvedge Jeans Straight.jpg',
      description: `Heavyweight 21oz American selvedge denim in classic straight fit. Built for decades of wear with exceptional fade potential. ${JEANS_SIZING_NOTE}`,
      quality: '21oz American selvedge denim, hidden rivets, leather patch',
      category: 'Bottoms',
      fit: 'Straight Fit'
    },
    {
      id: 'american-heavy-selvedge-tapered',
      name: 'Heavy American Selvedge Jeans - Straight Tapered',
      image: '/catalog/Heavy American Selvedge Jeans Tapered.jpg',
      description: `Heavyweight 21oz American selvedge denim with straight-tapered fit. Maximum durability meets modern silhouette. ${JEANS_SIZING_NOTE}`,
      quality: '21oz American selvedge denim, hidden rivets, leather patch',
      category: 'Bottoms',
      fit: 'Straight Tapered'
    },
    // Light Japanese Selvedge (14oz)
    {
      id: 'japanese-light-selvedge-straight',
      name: 'Light Japanese Selvedge Jeans - Straight Fit',
      image: '/catalog/Light Japanese Selvedge Jeans Straight.jpg',
      description: `Premium 14oz Japanese selvedge from legendary Okayama mills. Classic straight fit with signature Japanese craftsmanship. ${JEANS_SIZING_NOTE}`,
      quality: '14oz Japanese selvedge denim, sanforized, hidden rivets',
      category: 'Bottoms',
      fit: 'Straight Fit'
    },
    {
      id: 'japanese-light-selvedge-tapered',
      name: 'Light Japanese Selvedge Jeans - Straight Tapered',
      image: '/catalog/Light Japanese Selvedge Jeans Tapered.jpg',
      description: `Premium 14oz Japanese selvedge with contemporary straight-tapered silhouette. Japanese heritage, modern fit. ${JEANS_SIZING_NOTE}`,
      quality: '14oz Japanese selvedge denim, sanforized, hidden rivets',
      category: 'Bottoms',
      fit: 'Straight Tapered'
    },
    // Heavy Japanese Selvedge (21oz)
    {
      id: 'japanese-heavy-selvedge-straight',
      name: 'Heavy Japanese Selvedge Jeans - Straight Fit',
      image: '/catalog/Heavy Japanese Selvedge Jeans Straight.jpg',
      description: `Heavyweight 21oz Japanese selvedge denim in classic straight fit. The ultimate expression of Japanese denim craftsmanship. ${JEANS_SIZING_NOTE}`,
      quality: '21oz Japanese selvedge denim, rope-dyed indigo, copper rivets',
      category: 'Bottoms',
      fit: 'Straight Fit'
    },
    {
      id: 'japanese-heavy-selvedge-tapered',
      name: 'Heavy Japanese Selvedge Jeans - Straight Tapered',
      image: '/catalog/Heavy Japanese Selvedge Jeans Tapered.jpg',
      description: `Heavyweight 21oz Japanese selvedge with straight-tapered fit. Exceptional fade potential with modern silhouette. ${JEANS_SIZING_NOTE}`,
      quality: '21oz Japanese selvedge denim, rope-dyed indigo, copper rivets',
      category: 'Bottoms',
      fit: 'Straight Tapered'
    },
    // Chinos and Trousers
    {
      id: 'chinos-darkblue',
      name: 'Premium Chinos - Dark Blue',
      image: '/catalog/High Quality Chinos - Dark Blue.PNG',
      description: 'Refined chinos in versatile dark blue. The smart casual staple.',
      quality: 'Premium cotton twill, garment dyed, horn buttons',
      category: 'Bottoms'
    },
    {
      id: 'chinos-gray',
      name: 'Premium Chinos - Gray',
      image: '/catalog/High Quality Chinos - Gray.PNG',
      description: 'Neutral gray chinos that work with everything. Clean lines, quality construction.',
      quality: 'Premium cotton twill, garment dyed, horn buttons',
      category: 'Bottoms'
    },
    {
      id: 'trousers-charcoal',
      name: 'Tailored Trousers - Charcoal',
      image: '/catalog/High Quality Trousers - Charcoal.PNG',
      description: 'Impeccably tailored trousers in sophisticated charcoal. Office to evening ready.',
      quality: 'Wool-blend fabric, half-canvas construction',
      category: 'Bottoms'
    },
    {
      id: 'trousers-darkblue',
      name: 'Tailored Trousers - Dark Blue',
      image: '/catalog/High Quality Trousers - Dark Blue.PNG',
      description: 'Classic dark blue trousers with modern tailoring. Refined professionalism.',
      quality: 'Wool-blend fabric, half-canvas construction',
      category: 'Bottoms'
    },
    {
      id: 'trousers-gray',
      name: 'Tailored Trousers - Gray',
      image: '/catalog/High Quality Trousers - Gray.PNG',
      description: 'Versatile gray trousers that anchor any outfit. Quality that shows.',
      quality: 'Wool-blend fabric, half-canvas construction',
      category: 'Bottoms'
    }
  ],
  shoes: [
    {
      id: 'chelsea-boots',
      name: 'Full-Grain Chelsea Boots',
      image: '/catalog/Full Grain Chelsea Boots.PNG',
      description: 'Sleek Chelsea boots in premium full-grain leather. The predgy footwear essential.',
      quality: 'Full-grain calfskin, Goodyear welted, leather sole',
      category: 'Shoes'
    },
    {
      id: 'work-boots',
      name: 'Full-Grain Work Boots',
      image: '/catalog/Full Grain Work Boots.PNG',
      description: 'Heritage work boots built for life. Rugged character meets quality construction.',
      quality: 'Full-grain leather, Goodyear welted, Vibram sole',
      category: 'Shoes'
    },
    {
      id: 'chukka-boots',
      name: 'Shell Cordovan Chukka Boots',
      image: '/catalog/Shell Cordovan Chukka Boots.PNG',
      description: 'The finest leather in the world in a classic chukka silhouette. Investment footwear.',
      quality: 'Genuine shell cordovan, leather lined, Blake construction',
      category: 'Shoes'
    },
    {
      id: 'derby-shoes',
      name: 'Full-Grain Derby Shoes',
      image: '/catalog/Full Grain Derby Shoes.PNG',
      description: 'Classic derby shoes in premium leather. Versatile elegance for any occasion.',
      quality: 'Full-grain calfskin, Goodyear welted, leather sole',
      category: 'Shoes'
    },
    {
      id: 'penny-loafers',
      name: 'Full-Grain Penny Loafers',
      image: '/catalog/Full Grain Penny Loafers.PNG',
      description: 'Timeless penny loafers that bridge prep and casual. Essential versatility.',
      quality: 'Full-grain leather, hand-stitched moc toe',
      category: 'Shoes'
    },
    {
      id: 'wingtips',
      name: 'Full-Grain Wingtips',
      image: '/catalog/Full Grain Wingtips.PNG',
      description: 'Classic wingtip brogues with meticulous detailing. Refined statement footwear.',
      quality: 'Full-grain calfskin, Goodyear welted, leather sole',
      category: 'Shoes'
    },
    {
      id: 'sneakers-black',
      name: 'Leather Sneakers - Black',
      image: '/catalog/Full Grain Leather Sneakers - Black.PNG',
      description: 'Minimalist black sneakers in premium leather. Modern edge meets quality.',
      quality: 'Full-grain calfskin, Margom sole, hand-finished',
      category: 'Shoes'
    },
    {
      id: 'sneakers-white',
      name: 'Leather Sneakers - White',
      image: '/catalog/Full Grain Leather Sneakers - White.PNG',
      description: 'Clean white leather sneakers that elevate any outfit. Refined casual perfection.',
      quality: 'Full-grain calfskin, Margom sole, hand-finished',
      category: 'Shoes'
    }
  ],
  accessories: [
    {
      id: 'dive-watch',
      name: 'Boutique Dive Watch',
      image: '/catalog/Boutique Dive Watch.PNG',
      description: 'Professional dive watch with refined aesthetics. Swiss movement, 300m water resistance.',
      quality: 'Swiss automatic movement, sapphire crystal, 316L steel',
      category: 'Accessories'
    },
    {
      id: 'steel-watch',
      name: 'Boutique Steel Watch',
      image: '/catalog/Boutique Steel Watch.PNG',
      description: 'Clean steel timepiece for everyday elegance. The essential accessory.',
      quality: 'Japanese automatic movement, sapphire crystal',
      category: 'Accessories'
    },
    {
      id: 'dress-watch',
      name: 'Stainless Steel Dress Watch',
      image: '/catalog/Stainless Steel Dress Watch.PNG',
      description: 'Refined dress watch in polished steel. Timeless sophistication.',
      quality: 'Swiss quartz movement, sapphire crystal',
      category: 'Accessories'
    },
    {
      id: 'leather-watch',
      name: 'Leather Strap Dress Watch',
      image: '/catalog/Leather Strap Dress Watches.PNG',
      description: 'Classic dress watch on premium leather strap. Understated elegance.',
      quality: 'Swiss quartz movement, alligator strap',
      category: 'Accessories'
    },
    {
      id: 'dress-belt',
      name: 'Full-Grain Wide Dress Belt',
      image: '/catalog/Full Grain Wide Dress Belt.PNG',
      description: 'Premium full-grain leather belt with solid brass hardware. The finishing touch.',
      quality: 'Full-grain bridle leather, solid brass buckle',
      category: 'Accessories'
    },
    {
      id: 'backpack-1',
      name: 'Smooth Leather Backpack I',
      image: '/catalog/Smooth Leather Backpack I.PNG',
      description: 'Refined leather backpack for the modern professional. Function meets sophistication.',
      quality: 'Full-grain leather, padded laptop compartment',
      category: 'Accessories'
    },
    {
      id: 'backpack-2',
      name: 'Smooth Leather Backpack II',
      image: '/catalog/Smooth Leather Backpack II.PNG',
      description: 'Sleek leather backpack with minimalist design. Carry with confidence.',
      quality: 'Full-grain leather, brass hardware',
      category: 'Accessories'
    },
    {
      id: 'briefcase',
      name: 'Leather Briefcase',
      image: '/catalog/Smooth Leather Briefcase I.PNG',
      description: 'Classic briefcase in premium leather. Commands respect in any setting.',
      quality: 'Full-grain leather, reinforced handle, brass locks',
      category: 'Accessories'
    },
    {
      id: 'optical-glasses',
      name: 'Acetate Optical Glasses',
      image: '/catalog/Acetate Optical Glasses.PNG',
      description: 'Premium Italian acetate frames with classic styling. Refined vision.',
      quality: 'Italian acetate, spring hinges, demo lenses',
      category: 'Accessories'
    },
    {
      id: 'steel-glasses',
      name: 'Steel Optical Glasses',
      image: '/catalog/Steel Optical Glasses.PNG',
      description: 'Lightweight steel frames for minimalist sophistication. Modern clarity.',
      quality: 'Titanium-steel blend, adjustable nose pads',
      category: 'Accessories'
    },
    {
      id: 'aviators-blue',
      name: 'Aviator Sunglasses - Blue',
      image: '/catalog/Aviators - Blue-white.PNG',
      description: 'Classic aviators with blue-tinted lenses. Iconic style, quality construction.',
      quality: 'Metal frame, polarized lenses, UV protection',
      category: 'Accessories'
    },
    {
      id: 'aviators-gray',
      name: 'Aviator Sunglasses - Gray',
      image: '/catalog/Aviators - Gray-white.PNG',
      description: 'Timeless aviators with neutral gray lenses. The essential sunglass.',
      quality: 'Metal frame, polarized lenses, UV protection',
      category: 'Accessories'
    },
    {
      id: 'newsboy-cap',
      name: 'Satin-Lined Newsboy Cap',
      image: '/catalog/Satin-lined Newsboy Cap.PNG',
      description: 'Classic newsboy cap with protective satin lining. Heritage style elevated.',
      quality: 'Wool tweed, satin lining, leather sweatband',
      category: 'Accessories'
    },
    {
      id: 'fountain-pen',
      name: 'Premium Fountain Pen',
      image: '/catalog/Fountain Pen.PNG',
      description: 'Fine writing instrument for the discerning gentleman. Make your mark.',
      quality: 'Brass body, 18k gold nib, converter included',
      category: 'Accessories'
    },
    {
      id: 'ballpoint-pen',
      name: 'Premium Ballpoint Pen',
      image: '/catalog/Ballpoint Pen.PNG',
      description: 'Everyday writing elevated. Quality construction for lasting use.',
      quality: 'Stainless steel body, twist mechanism',
      category: 'Accessories'
    },
    {
      id: 'umbrella',
      name: "Gentleman's Umbrella",
      image: '/catalog/Umbrella.PNG',
      description: 'Classic umbrella with quality construction. Be prepared with style.',
      quality: 'Wooden handle, steel frame, double canopy',
      category: 'Accessories'
    }
  ]
};

const categories = [
  { id: 'all', label: 'All', icon: '◆' },
  { id: 'tops', label: 'Tops', icon: '◆' },
  { id: 'midLayers', label: 'Mid Layers', icon: '◆' },
  { id: 'outerwear', label: 'Outerwear', icon: '◆' },
  { id: 'bottoms', label: 'Bottoms', icon: '◆' },
  { id: 'shoes', label: 'Shoes', icon: '◆' },
  { id: 'accessories', label: 'Accessories', icon: '◆' }
];

const Catalog = ({ activeTab, setActiveTab }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const getAllProducts = () => {
    return Object.values(catalogData).flat();
  };

  const getFilteredProducts = () => {
    if (activeTab === 'all') {
      return getAllProducts();
    }
    return catalogData[activeTab] || [];
  };

  return (
    <section id="catalog" className="catalog">
      <div className="catalog-container">
        <motion.div 
          className="catalog-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-eyebrow">The Collection</span>
          <h2 className="section-title">
            Curated <span className="highlight">Catalog</span>
          </h2>
          <div className="steel-line center"></div>
          <p className="catalog-intro">
            Every piece selected for quality, character, and timeless appeal. 
            Coming soon to elevate your wardrobe.
          </p>
        </motion.div>

        {/* Category Tabs */}
        <div className="catalog-tabs">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`tab-button ${activeTab === category.id ? 'active' : ''}`}
              onClick={() => setActiveTab(category.id)}
            >
              <span className="tab-icon">{category.icon}</span>
              <span className="tab-label">{category.label}</span>
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <motion.div 
          className="product-grid"
          layout
        >
          <AnimatePresence mode="popLayout">
            {getFilteredProducts().map((product, index) => (
              <motion.div
                key={product.id}
                className="product-card"
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="product-image">
                  <img 
                    src={process.env.PUBLIC_URL + product.image} 
                    alt={product.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="product-overlay">
                    <span className="view-details">View Details</span>
                  </div>
                </div>
                <div className="product-info">
                  <span className="product-category">{product.category}</span>
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-quality">{product.quality}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Coming Soon Notice */}
        <motion.div 
          className="coming-soon-notice"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="notice-content">
            <span className="notice-icon">◆</span>
            <p>
              <strong>Coming Soon</strong> — We're curating the finest pieces for our launch. 
              Follow us for updates on availability.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Product Detail Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div 
            className="product-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProduct(null)}
          >
            <motion.div 
              className="product-modal"
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="modal-close"
                onClick={() => setSelectedProduct(null)}
              >
                ×
              </button>
              <div className="modal-image">
                <img 
                  src={process.env.PUBLIC_URL + selectedProduct.image} 
                  alt={selectedProduct.name} 
                />
              </div>
              <div className="modal-content">
                <span className="modal-category">{selectedProduct.category}</span>
                <h3 className="modal-name">{selectedProduct.name}</h3>
                <p className="modal-description">{selectedProduct.description}</p>
                <div className="modal-quality">
                  <span className="quality-label">Quality Notes</span>
                  <p>{selectedProduct.quality}</p>
                </div>
                <div className="modal-status">
                  <span className="status-badge">Coming Soon</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Catalog;
